import { api, extractTokens } from './api.js';
import { router } from './router.js';

/**
 * Decodes a JWT payload without verifying it. Safe here *only* because nothing security
 * relevant depends on the result: the API Gateway JWT authorizer verifies every token
 * server-side, and admin routes re-check `cognito:groups` in-handler. This is purely so
 * the UI can hide controls the user can't use.
 */
function decodeJwtPayload(token) {
    try {
        const [, payload] = token.split('.');
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decodeURIComponent(escape(json)));
    } catch {
        return null;
    }
}

class AuthManager {
    constructor() {
        this.user = null;
        this.groups = [];
        this.isAuthenticated = false;
    }

    /** Restores a session on page load from the stored refresh token. */
    async init() {
        if (!api.hasSession()) {
            this.#clear();
            return false;
        }
        const refreshed = await api.refreshSession();
        if (!refreshed) {
            this.#clear();
            api.clearSession();
            return false;
        }
        try {
            await this.loadUser();
            return true;
        } catch {
            this.#clear();
            api.clearSession();
            return false;
        }
    }

    /**
     * Starts a login. Returns either {status:'ok'} or {status:'challenge', ...} —
     * Cognito uses one challenge mechanism for both forced password change and TOTP,
     * so the caller decides which form to show.
     */
    async login(email, password) {
        const data = await api.login(email, password);

        if (data.kind === 'challenge') {
            return {
                status: 'challenge',
                challengeName: data.challenge.challengeName,
                session: data.challenge.session,
                email,
            };
        }
        const tokens = extractTokens(data);
        if (!tokens) throw new Error('Sign in did not return a session');
        await this.#establish(tokens);
        return { status: 'ok' };
    }

    /** Completes a NEW_PASSWORD_REQUIRED or SOFTWARE_TOKEN_MFA challenge. */
    async completeChallenge(payload) {
        const data = await api.respondToChallenge(payload);
        if (data.kind === 'challenge') {
            // MFA can follow a password change: surface the next challenge rather than
            // assuming a challenge response always yields tokens.
            return {
                status: 'challenge',
                challengeName: data.challenge.challengeName,
                session: data.challenge.session,
                email: payload.email,
            };
        }
        const tokens = extractTokens(data);
        if (!tokens) throw new Error('That step did not return a session');
        await this.#establish(tokens);
        return { status: 'ok' };
    }

    async #establish(tokens) {
        api.setSession(tokens);
        const claims = decodeJwtPayload(tokens.AccessToken) ?? {};
        this.groups = claims['cognito:groups'] ?? [];
        await this.loadUser();
        window.dispatchEvent(new CustomEvent('auth:login', { detail: this.user }));
    }

    /** Loads the app-side profile. Cognito access tokens carry sub/groups only, never the email. */
    async loadUser() {
        this.user = await api.getProfile();
        this.isAuthenticated = true;
        if (api.accessToken) {
            this.groups = decodeJwtPayload(api.accessToken)?.['cognito:groups'] ?? this.groups;
        }
        return this.user;
    }

    async logout() {
        try {
            await api.logout();
        } catch {
            // An expired token makes this fail; the local session is being torn down anyway.
        }
        this.#clear();
        api.clearSession();
        window.dispatchEvent(new CustomEvent('auth:logout'));
        // The public landing page, not the sign-in form: someone who has just signed out has
        // said they are done, and the form is what they would see if they wanted back in.
        // A session that *expires* still lands on /login -- there the intent is the opposite.
        router.navigate('/');
    }

    #clear() {
        this.user = null;
        this.groups = [];
        this.isAuthenticated = false;
    }

    isAdmin() {
        return this.isAuthenticated && this.groups.includes('admins');
    }

    /** No sharing and no one else's events; the API enforces it, this only hides what it would refuse. */
    isLimited() {
        return this.isAuthenticated && this.user?.account_type === 'limited';
    }

    getUser() {
        return this.user;
    }

    setUser(user) {
        this.user = user;
    }
}

export const auth = new AuthManager();
